import { useMemo, useState, useEffect } from 'react';
import { Clock, Building2, Package, Link2, Users, Loader2, RefreshCw } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProgressIndicator } from './ProgressIndicator';
import { ModuleProgressCard } from './ModuleProgressCard';
import { LogStream } from './LogStream';
import { cn, formatEnumStatus } from '@/lib/utils';
import type { Migration, MigrationLog, MigrationModule, ModuleConfiguration } from '@/types';
import type { ProgressUpdate, UserMigrationPhaseState } from '@/hooks/useMigrationProgress';

interface ProgressDashboardProps {
  migration: Migration;
  progress: ProgressUpdate | null;
  logs: MigrationLog[];
  modulesConfig: ModuleConfiguration[];
  onRetryModule?: ((moduleKey: string) => void) | undefined;
  isRetrying?: boolean | undefined;
  userMigrationPhase?: UserMigrationPhaseState | undefined;
  onRetryUserMigration?: (() => void) | undefined;
  isRetryingUserMigration?: boolean | undefined;
}

function formatElapsedTime(startDate: string): string {
  const start = new Date(startDate).getTime();
  const now = Date.now();
  const elapsed = Math.floor((now - start) / 1000);

  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function calculateOverallProgress(modules: MigrationModule[]): number {
  if (modules.length === 0) return 0;

  let totalProgress = 0;
  for (const mod of modules) {
    if (mod.migrateStatus === 'Succeeded' || mod.migrateStatus === 'Skipped') totalProgress += 100;
    else if (mod.migrateStatus === 'InProgress') totalProgress += 50;
  }

  return Math.round(totalProgress / modules.length);
}

function getCurrentOperation(modules: MigrationModule[]): string {
  for (const mod of modules) {
    if (mod.migrateStatus === 'InProgress') {
      return `Migrating: ${mod.moduleKey}`;
    }
  }

  const pending = modules.filter((m) => m.migrateStatus === 'Pending');
  if (pending.length > 0) {
    return 'Preparing next module...';
  }

  return 'Processing...';
}

function getCompletedSteps(modules: MigrationModule[]): { current: number; total: number } {
  const total = modules.length;
  let current = 0;

  for (const mod of modules) {
    if (
      mod.migrateStatus === 'Succeeded' ||
      mod.migrateStatus === 'Failed' ||
      mod.migrateStatus === 'Skipped'
    )
      current++;
  }

  return { current, total };
}

export function ProgressDashboard({
  migration,
  progress,
  logs,
  modulesConfig,
  onRetryModule,
  isRetrying,
  userMigrationPhase,
  onRetryUserMigration,
  isRetryingUserMigration,
}: ProgressDashboardProps) {
  const [elapsedTime, setElapsedTime] = useState('0s');

  // Update elapsed time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(formatElapsedTime(migration.startedAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [migration.startedAt]);

  const overallProgress = useMemo(
    () => calculateOverallProgress(migration.modules),
    [migration.modules]
  );

  const currentOperation = useMemo(() => {
    if (userMigrationPhase?.status === 'collecting') return 'Collecting users…';
    if (userMigrationPhase?.status === 'onboarding') {
      const { onboarded, failed, total } = userMigrationPhase;
      return `Onboarding users: ${onboarded + failed} / ${total}`;
    }
    return progress?.message ?? getCurrentOperation(migration.modules);
  }, [progress, migration.modules, userMigrationPhase]);

  const { current: currentStep, total: totalSteps } = useMemo(
    () => getCompletedSteps(migration.modules),
    [migration.modules]
  );

  const moduleKeys = useMemo(
    () => migration.modules.map((m) => m.moduleKey),
    [migration.modules]
  );

  const getModuleName = (moduleKey: string) => {
    return modulesConfig.find((c) => c.moduleKey === moduleKey)?.displayName ?? moduleKey;
  };

  const errorCount = logs.filter((l) => l.level === 'Error').length;

  return (
    <div className="space-y-6">
      {/* Header with organization info */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">{migration.organizationName}</h2>
            <p className="text-sm text-muted-foreground">
              {migration.organizationType === 'New' ? 'New Organization' : 'Existing Organization'}
              {migration.bridgeEntityName && (
                <span className="inline-flex items-center gap-1 ml-2">
                  <Link2 className="h-3 w-3" />
                  {migration.bridgeEntityName}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="text-sm">Elapsed: {elapsedTime}</span>
          </div>
          <Badge variant="outline" className="gap-1">
            <Package className="h-3 w-3" />
            {migration.modules.length} modules
          </Badge>
        </div>
      </div>

      {/* Main progress section */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Progress indicator */}
        <Card className="md:col-span-1">
          <CardContent className="flex items-center justify-center py-8">
            <ProgressIndicator
              percentage={overallProgress}
              currentStep={currentStep}
              totalSteps={totalSteps}
              currentOperation={currentOperation}
              size="lg"
            />
          </CardContent>
        </Card>

        {/* Modules overview */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Module Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {migration.modules.map((mod) => {
                const isActive = mod.migrateStatus === 'InProgress';
                const isComplete =
                  mod.migrateStatus === 'Succeeded' || mod.migrateStatus === 'Skipped';
                const isFailed = mod.migrateStatus === 'Failed';

                return (
                  <div
                    key={mod.moduleKey}
                    className={cn(
                      'flex items-center justify-between rounded-lg border p-3',
                      isActive && 'border-primary bg-primary/5',
                      isComplete && 'border-success/50 bg-success/5',
                      isFailed && 'border-destructive/50 bg-destructive/5'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{getModuleName(mod.moduleKey)}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {mod.migrateStatus === 'Pending' && 'Pending'}
                      {mod.migrateStatus === 'InProgress' && 'Migrating...'}
                      {mod.migrateStatus === 'Succeeded' && 'Complete'}
                      {mod.migrateStatus === 'Skipped' && 'Skipped'}
                      {mod.migrateStatus === 'Failed' && 'Failed'}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase 2: User Migration */}
      {(userMigrationPhase?.status !== 'idle' ||
        migration.userMigrationPhaseStatus === 'InProgress' ||
        migration.userMigrationPhaseStatus === 'Succeeded' ||
        migration.userMigrationPhaseStatus === 'Partial' ||
        migration.userMigrationPhaseStatus === 'Failed') && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Users className="h-5 w-5 text-muted-foreground shrink-0" />
              <CardTitle className="text-lg">Phase 2: User Migration</CardTitle>
              {userMigrationPhase?.status === 'collecting' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <Badge variant="secondary">Collecting users…</Badge>
                </>
              )}
              {userMigrationPhase?.status === 'onboarding' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <Badge variant="secondary">
                    Onboarding: {userMigrationPhase.onboarded + userMigrationPhase.failed} / {userMigrationPhase.total}
                  </Badge>
                </>
              )}
              {userMigrationPhase?.status === 'complete' && (
                <Badge
                  variant={
                    userMigrationPhase.phaseStatus === 'Succeeded'
                      ? 'default'
                      : userMigrationPhase.phaseStatus === 'Partial'
                        ? 'secondary'
                        : 'destructive'
                  }
                >
                  {formatEnumStatus(userMigrationPhase.phaseStatus)}
                </Badge>
              )}
              {/* Fallback badge — shown when page loads after Phase 2 already finished (no SignalR events this session) */}
              {userMigrationPhase?.status === 'idle' &&
                migration.userMigrationPhaseStatus !== 'InProgress' &&
                migration.userMigrationPhaseStatus !== 'NotApplicable' && (
                <Badge
                  variant={
                    migration.userMigrationPhaseStatus === 'Succeeded'
                      ? 'default'
                      : migration.userMigrationPhaseStatus === 'Partial'
                        ? 'secondary'
                        : 'destructive'
                  }
                >
                  {formatEnumStatus(migration.userMigrationPhaseStatus)}
                </Badge>
              )}
              {userMigrationPhase?.status === 'idle' &&
                migration.userMigrationPhaseStatus === 'InProgress' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <Badge variant="secondary">In progress…</Badge>
                </>
              )}
              {(migration.userMigrationPhaseStatus === 'Partial' ||
                migration.userMigrationPhaseStatus === 'Failed') &&
                userMigrationPhase?.status !== 'collecting' &&
                userMigrationPhase?.status !== 'onboarding' &&
                onRetryUserMigration && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetryUserMigration}
                  disabled={isRetryingUserMigration}
                  aria-busy={isRetryingUserMigration}
                  className="ml-auto"
                >
                  {isRetryingUserMigration ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  {isRetryingUserMigration ? 'Retrying…' : 'Retry User Migration'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Live progress bar during onboarding */}
            {userMigrationPhase?.status === 'onboarding' && userMigrationPhase.total > 0 && (
              <div className="space-y-1">
                <Progress
                  value={Math.round(
                    ((userMigrationPhase.onboarded + userMigrationPhase.failed) / userMigrationPhase.total) * 100
                  )}
                  className="h-2"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {Math.round(
                    ((userMigrationPhase.onboarded + userMigrationPhase.failed) / userMigrationPhase.total) * 100
                  )}% processed
                </p>
              </div>
            )}

            {/* Collecting skeleton */}
            {userMigrationPhase?.status === 'collecting' && (
              <div className="space-y-2">
                <div className="h-2 rounded bg-muted animate-pulse" />
                <p className="text-xs text-muted-foreground">Gathering users from source systems…</p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              {/* Counts are only meaningful when live SignalR data has arrived (status !== 'idle').
                  On page load the initial state is all-zeros — show '—' until real data flows in. */}
              <div>
                <p className="text-muted-foreground">Unique Users</p>
                <p className="font-medium">
                  {userMigrationPhase?.status !== 'idle' && (userMigrationPhase?.totalUnique ?? 0) > 0
                    ? userMigrationPhase?.totalUnique
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Onboarded</p>
                <p className="font-medium">
                  {userMigrationPhase?.status !== 'idle' ? (userMigrationPhase?.onboarded ?? '—') : '—'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Failed</p>
                <p className={cn('font-medium', userMigrationPhase?.status !== 'idle' && (userMigrationPhase?.failed ?? 0) > 0 && 'text-destructive')}>
                  {userMigrationPhase?.status !== 'idle' ? (userMigrationPhase?.failed ?? '—') : '—'}
                </p>
              </div>
              {userMigrationPhase?.status === 'complete' && (
                <div>
                  <p className="text-muted-foreground">Invites Sent</p>
                  <p className="font-medium">{userMigrationPhase.invitesSent}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed view tabs */}
      <Tabs defaultValue="modules">
        <TabsList>
          <TabsTrigger value="modules">Module Details</TabsTrigger>
          <TabsTrigger value="logs">
            Logs
            {errorCount > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                {errorCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="modules" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {migration.modules.map((mod) => (
              <ModuleProgressCard
                key={mod.moduleKey}
                module={mod}
                moduleName={getModuleName(mod.moduleKey)}
                onRetry={onRetryModule}
                isRetrying={isRetrying}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <LogStream logs={logs} moduleKeys={moduleKeys} maxHeight="500px" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
